import { type CSSProperties, useEffect, useState } from 'react'
import { fetchSheet } from '../lib/print'
import { Notice } from './index'
import './sheet-proof.css'

type SheetProofStyle = CSSProperties & {
  '--sheet-proof-page-width': string
  '--sheet-proof-page-ratio': string
}

/**
 * The printed sheet, as the printed sheet.
 *
 * A preview drawn from the same component and stylesheet is already close, and
 * close is exactly the failure mode: the two renderings agree until one of them
 * quietly does not, and nothing announces it. So this asks the service for the
 * document and shows THAT — one artifact, seen rather than approximated, and no
 * second layout to keep in step.
 *
 * The cost is that it is a request: a render takes about a second, and every
 * option is a new one. That is why it is what the preview shows and not what
 * the screen always shows — the live sheet answers while somebody is still
 * deciding, and this answers what comes out.
 */
export function SheetProof({
  url,
  flowing,
  page,
}: {
  url: string
  flowing: boolean
  /**
   * The finished page in inches, Letter where none is named. A flowing sheet
   * takes its width, and its height is the window it scrolls in; a sheet of
   * one page takes its proportions too.
   */
  page?: { width: number; height: number }
}) {
  const [href, setHref] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    let held: string | null = null
    setHref(null)
    setFailed(null)
    fetchSheet(url)
      .then((made) => {
        held = made
        if (live) setHref(made)
        else URL.revokeObjectURL(made)
      })
      .catch((err: Error) => {
        if (live) setFailed(err.message)
      })
    return () => {
      live = false
      if (held) URL.revokeObjectURL(held)
    }
  }, [url])

  /* The refusal is read before it is shown, so a sheet the server would not
     give — no session, no permission, no renderer — says so in the app's own
     words rather than arriving inside the frame looking like a page. */
  if (failed) return <Notice tone="error">{failed}</Notice>
  if (!href) return <Notice>Rendering the sheet…</Notice>

  const geometry: SheetProofStyle | undefined = page
    ? {
        '--sheet-proof-page-width': `${page.width}in`,
        '--sheet-proof-page-ratio': `${page.width} / ${page.height}`,
      }
    : undefined

  return (
    <div className="sheet-proof" style={geometry}>
      <div
        className={`sheet-proof__page${
          flowing ? ' sheet-proof__page--flowing' : ''
        }`}
      >
        <iframe
          className="sheet-proof__doc"
          /* The viewer's own chrome is a second set of controls over a sheet
             this page already has controls for. A wall sheet is one page and
             the frame is shaped to hold it, so it opens showing the whole of
             it; a sheet that FLOWS has pages this frame cannot promise, and
             keeps the viewer's own way of moving between them. */
          src={`${href}#toolbar=0${flowing ? '' : '&navpanes=0&view=Fit'}`}
          title="The printed sheet"
        />
      </div>
    </div>
  )
}
